// Pure display rules for the quote drawer (#1559). No React: every rule here
// is unit-tested in quote-drawer-logic.test.ts.
import { format, parseISO } from "date-fns";
import type { QuoteRow } from "@/hooks/useQuotes";
import type { ContactMessage } from "@/hooks/useContactMessages";
import type { PillTone } from "@/lib/social-scoreboard";
import { formatRelativeTime } from "@/lib/format-relative-time";
import { describeProvenance } from "@/lib/provenance";
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from "@/types/quotes";

export interface PillSpec { tone: PillTone; label: string }
export interface PaymentSummary { paid: number; balance: number; pct: number; pendingAmount: number }
export interface QuoteResponse { action: string; message: string | null; created_at: string; respondent_name?: string | null }
export interface PaymentEntry { amount: number; method: string; payment_date: string }
export interface NextStepPart { text: string; tone?: "strong" | "hot" | "quote" }

export type NextStepId = "reopenEvent" | "preview" | "send" | "inbox" | "editResend" | "copy" | "resend" | "followUp"
  | "duplicate" | "reopenQuote" | "collect" | "record";
export interface NextStepButton { id: NextStepId; label: string; primary: boolean; disabled?: boolean }
export interface NextStep { parts: NextStepPart[]; buttons: NextStepButton[]; blockedNote?: string }

// `total` is the one the drawer shows (live while editing); `dirty` is anything
// unsaved, the quote draft or the Edit details draft.
export interface NextStepInput {
  quote: QuoteRow; itemCount: number; total: number; summary: PaymentSummary; responses: QuoteResponse[]; dirty: boolean; today?: Date;
}

export type MenuEntryId = "preview" | "markSent" | "copySend" | "copy" | "resend" | "markAccepted" | "markDeclined"
  | "backToDraft" | "reopenQuote" | "duplicate" | "pdf" | "saveTemplate" | "viewSquare" | "resync" | "deleteDraft" | "delete";
export interface MenuEntry { id: MenuEntryId; label: string; sub?: string; danger?: boolean; disabled?: boolean }

// `deletable` is useDraftDeletable's answer, undefined while it loads.
export interface MenuInput { quote: QuoteRow; itemCount: number; deletable: boolean | undefined; dirty: boolean }

export const BLOCKED_WHILE_DIRTY = "Save or discard your changes first";

const MONEY = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });
const num = (x: unknown) => Number(x) || 0;
const cents = (n: number) => Math.round(n * 100) / 100;

/** "$3,013.50". */
export function formatMoney(n: number): string {
  return MONEY.format(num(n));
}

/** "Sep 10" from a yyyy-mm-dd date (read as a local date) or a timestamp. */
export function formatDay(value: string | null | undefined): string {
  const d = value ? parseISO(value) : null;
  return d && !Number.isNaN(d.getTime()) ? format(d, "MMM d") : "";
}

export function firstName(clientName: string | null | undefined): string {
  return clientName?.trim().split(/\s+/)[0] || "the client";
}

export function canEditQuote(q: Pick<QuoteRow, "status" | "square_source">): boolean {
  return !q.square_source && q.status !== "accepted";
}

/** The prototype opens a draft straight into edit mode. */
export function opensEditing(q: Pick<QuoteRow, "status" | "square_source">): boolean {
  return canEditQuote(q) && q.status === "draft";
}

const STATUS_PILLS: Record<string, PillSpec> = {
  draft: { tone: "mute", label: "Draft" }, sent: { tone: "info", label: "Sent" },
  accepted: { tone: "ok", label: "Accepted" }, declined: { tone: "hot", label: "Declined" },
};

export function statusPill(status: string): PillSpec {
  return STATUS_PILLS[status] ?? { tone: "mute", label: status };
}

/** Only accepted quotes get a payment pill. `paid` is summarizePayments().paid. */
export function paymentPill(status: string, paid: number, total: number): PillSpec | null {
  if (status !== "accepted") return null;
  if (paid <= 0) return { tone: "warn", label: "Unpaid" };
  return num(total) - paid <= 0.005 ? { tone: "ok", label: "Paid" } : { tone: "warn", label: "Part paid" };
}

/**
 * Paid comes from quotes.amount_paid, which the update_quote_amount_paid
 * trigger keeps as the sum of real quote_payments rows. A pending e-transfer
 * is reported on its own and never added: it isn't paid until marked received.
 */
export function summarizePayments({ total, amountPaid, pendingAmount }: {
  total: number | null | undefined; amountPaid: number | null | undefined; pendingAmount?: number | null;
}): PaymentSummary {
  const t = num(total);
  const paid = num(amountPaid);
  const pct = t > 0 ? Math.min(100, Math.max(0, (paid / t) * 100)) : 0;
  return { paid, balance: Math.max(0, t - paid), pct, pendingAmount: num(pendingAmount) };
}

const localDay = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());

/** Whole days a balance is past its due date, or null when it isn't overdue. */
export function overdueDays(due: string | null | undefined, balance: number, today: Date = new Date()): number | null {
  if (!due || balance <= 0.005) return null;
  const d = parseISO(due);
  const days = Number.isNaN(d.getTime()) ? 0 : Math.round((localDay(today) - localDay(d)) / 86_400_000);
  return days > 0 ? days : null;
}

function owingParts(q: QuoteRow, s: PaymentSummary, today: Date | undefined): NextStepPart[] {
  const word = s.paid > 0 ? "balance" : "owing";
  const parts: NextStepPart[] = s.paid > 0 ? [{ text: `${formatMoney(s.paid)} paid · ` }] : [];
  parts.push({ text: formatMoney(s.balance), tone: "strong" });
  const late = overdueDays(q.payment_due_date, s.balance, today);
  if (late) parts.push({ text: ` ${word} ` }, { text: `${late} days overdue`, tone: "hot" }, { text: "." });
  else if (q.payment_due_date) parts.push({ text: ` ${word} due ${formatDay(q.payment_due_date)}.` });
  else parts.push({ text: s.paid > 0 ? " balance due." : " owing." });
  return parts;
}

function nextStepFor(input: NextStepInput): NextStep {
  const { quote: q, itemCount, total, summary, responses, dirty, today } = input;
  const first = firstName(q.client_name);
  const btn = (id: NextStepId, label: string, primary = false): NextStepButton => ({ id, label, primary });
  const say = (text: string, ...buttons: NextStepButton[]): NextStep => ({ parts: [{ text }], buttons });

  if (q.is_lost) return say("This event is marked lost.", btn("reopenEvent", "Re-open event", true));
  if (q.status === "draft") {
    if (itemCount === 0) return say(`Add what ${first} is getting, then send it.`);
    return say(
      `${itemCount} item${itemCount === 1 ? "" : "s"} · ${formatMoney(total)}. Ready to send when you are.`,
      btn("preview", "Preview as client"),
      btn("send", dirty ? "Save and send" : `Send to ${first}`, true),
    );
  }
  if (q.status === "sent") {
    const r = responses.reduce<QuoteResponse | null>((a, x) => (!a || x.created_at > a.created_at ? x : a), null);
    if (r?.action !== "revision_requested") {
      return say("Sent. No answer yet.", btn("copy", "Copy link"), btn("resend", "Resend"), btn("followUp", "Follow up", true));
    }
    const parts: NextStepPart[] = [{ text: `${first} asked for a change`, tone: "strong" }, { text: ` on ${formatDay(r.created_at)}:` }];
    if (r.message?.trim()) parts.push({ text: r.message.trim(), tone: "quote" });
    return { parts, buttons: [btn("inbox", "Reply in Inbox"), btn("editResend", "Edit and resend", true)] };
  }
  if (q.status === "declined") {
    return say(`${first} declined this quote.`, btn("duplicate", "Duplicate"), btn("reopenQuote", "Re-open quote", true));
  }
  if (summary.balance <= 0.005) return say("Paid in full. Nothing owing.", btn("copy", "Copy link"));
  const buttons = [btn("collect", "Collect payment"), btn("record", "Record payment", true)];
  return { parts: owingParts(q, summary, today), buttons };
}

const DIRTY_BLOCKED_STEPS: ReadonlySet<NextStepId> = new Set(["reopenQuote", "duplicate"]);

/** The Next step box: the message and its buttons for the quote's state. */
export function resolveNextStep(input: NextStepInput): NextStep {
  const step = nextStepFor(input);
  if (!input.dirty || !step.buttons.some((b) => DIRTY_BLOCKED_STEPS.has(b.id))) return step;
  const buttons = step.buttons.map((b) => (DIRTY_BLOCKED_STEPS.has(b.id) ? { ...b, disabled: true } : b));
  return { ...step, buttons, blockedNote: BLOCKED_WHILE_DIRTY };
}

const DIRTY_BLOCKED_ENTRIES: ReadonlySet<MenuEntryId> = new Set([
  "markAccepted", "markDeclined", "backToDraft", "reopenQuote", "duplicate", "resync",
]);

function deleteEntry(q: QuoteRow, itemCount: number, deletable: boolean | undefined): MenuEntry {
  if (q.status !== "draft") return { id: "delete", label: "Delete", sub: "Only drafts can be deleted", disabled: true };
  const del = (sub: string, disabled?: boolean): MenuEntry => ({ id: "deleteDraft", label: "Delete draft", sub, danger: true, disabled });
  if (num(q.amount_paid) > 0) return del("It has a payment, so it stays", true);
  if (deletable === false) return del("It's the event's only quote, so it stays", true);
  return del(itemCount === 0 ? "Removes it from the event" : "Asks first; it has items");
}

/** The ⋯ menu, in the prototype's order. "sep" is a separator. */
export function menuEntries({ quote: q, itemCount, deletable, dirty }: MenuInput): (MenuEntry | "sep")[] {
  const first = firstName(q.client_name);
  const rows: (MenuEntry | "sep")[] = [{ id: "preview", label: "Preview as client", sub: `What ${first} sees` }];
  if (q.status !== "draft") rows.push({ id: "copy", label: "Copy client link" });
  else {
    rows.push({ id: "markSent", label: "Mark as sent", sub: "You sent it yourself. Nothing goes out." },
      { id: "copySend", label: "Copy link and mark sent", sub: "Paste it in a text or email" });
  }
  if (q.status === "sent") {
    rows.push({ id: "resend", label: "Resend", sub: "Copies the link to paste in a message" },
      { id: "markAccepted", label: "Mark accepted", sub: `${first} said yes by phone or text` },
      { id: "markDeclined", label: "Mark declined" }, { id: "backToDraft", label: "Back to draft", sub: "Edit it before it goes out again" });
  }
  if (q.status === "accepted" && !q.square_source && num(q.amount_paid) === 0) {
    rows.push({ id: "backToDraft", label: "Back to draft", sub: "Unlocks it for editing. Only before any payment." });
  }
  if (q.status === "declined") rows.push({ id: "reopenQuote", label: "Re-open quote" });
  const template: MenuEntry = itemCount > 0
    ? { id: "saveTemplate", label: "Save as template", sub: "Reuse these items on other quotes" }
    : { id: "saveTemplate", label: "Save as template", sub: "Add items first", disabled: true };
  rows.push("sep", { id: "duplicate", label: "Duplicate", sub: "Same items, new draft" }, { id: "pdf", label: "Download PDF" }, template);
  if (q.square_source && q.square_invoice_id) {
    const resync: MenuEntry = { id: "resync", label: "Resync from Square", sub: "Pulls the latest items and payments" };
    rows.push("sep", { id: "viewSquare", label: "View in Square" }, resync);
  }
  rows.push("sep", deleteEntry(q, itemCount, deletable));
  if (!dirty) return rows;
  return rows.map((r) => (r !== "sep" && DIRTY_BLOCKED_ENTRIES.has(r.id) ? { ...r, sub: BLOCKED_WHILE_DIRTY, disabled: true } : r));
}

/** Collect payment's amount presets, as [label, amount]. */
export function paymentPresets({ total, paid, depositPct }: { total: number; paid: number; depositPct: number }): [string, number][] {
  const balance = Math.max(0, total - paid);
  if (paid > 0) return [["Remaining", cents(balance)], ["50% of balance", cents(balance / 2)]];
  const presets: [string, number][] = [[`Deposit ${depositPct}%`, cents((total * depositPct) / 100)], ["50%", cents(total / 2)], ["Full", cents(total)]];
  return presets.filter(([, amount]) => amount > 0);
}

/** The deposit_percent sent to get-payment-link; undefined asks for the full amount. */
export function paymentLinkPercent(amount: number, total: number): number | undefined {
  const pct = Math.round((amount / total) * 10000) / 100;
  return Number.isFinite(pct) && pct < 100 ? pct : undefined;
}

export function methodLabel(method: string): string {
  return PAYMENT_METHOD_LABELS[method as PaymentMethod] ?? method;
}

export function deletePaymentPrompt(amount: number, method: string): string {
  return `Delete this ${formatMoney(amount)} ${methodLabel(method)} payment? The balance goes back up.`;
}

const RESPONSE_TEXT: Record<string, string> = { accepted: "accepted", declined: "declined", revision_requested: "asked for a change" };

/** Messages & activity: [day, text] rows, newest first. */
export function buildActivity({ quote, responses, payments, pendingAmount, pendingAt }: {
  quote: QuoteRow; responses: QuoteResponse[]; payments: PaymentEntry[]; pendingAmount?: number | null; pendingAt?: string | null;
}): [string, string][] {
  const first = firstName(quote.client_name);
  const rows: { at: string; text: string }[] = [];
  if (quote.created_at) {
    const p = describeProvenance({ name: quote.created_by_profile?.display_name, via: quote.created_via, at: quote.created_at });
    rows.push({ at: quote.created_at, text: `${p.lead} ${p.subject}` });
  }
  for (const r of responses) rows.push({ at: r.created_at, text: `${first} ${RESPONSE_TEXT[r.action] ?? r.action}` });
  for (const p of payments) rows.push({ at: p.payment_date, text: `${formatMoney(num(p.amount))} recorded (${methodLabel(p.method)})` });
  if (num(pendingAmount) > 0 && pendingAt) rows.push({ at: pendingAt, text: `Waiting on an e-transfer of ${formatMoney(num(pendingAmount))}` });
  rows.sort((a, b) => parseISO(b.at).getTime() - parseISO(a.at).getTime());
  return rows.map((r) => [formatDay(r.at), r.text]);
}

const VERB: Record<string, string> = { sms: "texted", email: "emailed", call: "called" };

/** The one-line summary of the latest message with the client. */
export function lastMessageLine(msg: ContactMessage | null | undefined, first: string): string {
  if (!msg) return "No messages stored yet.";
  const when = formatRelativeTime(msg.sent_at) ?? "";
  const verb = VERB[msg.channel] ?? "messaged";
  if (msg.direction === "outbound") return `You ${verb} ${first} ${when}.`;
  const body = (msg.body ?? msg.subject ?? "").replace(/\s+/g, " ").trim();
  if (!body) return `${first} ${verb} ${when}.`;
  return `${first} ${verb} ${when}: "${body.length > 140 ? `${body.slice(0, 139)}…` : body}"`;
}

export function lastContacted(contact: { last_message_at: string | null } | null | undefined): string {
  return formatRelativeTime(contact?.last_message_at) ?? "No messages yet";
}

// Every value quotes_source_check allows.
const SOURCE_LABELS: Record<string, string> = {
  manual: "Added by staff", online_order: "Online order", marketing_cta: "Website form", import: "Imported", plan_wizard: "Plan wizard",
};

type AttributionFields = "square_source" | "source" | "utm_source" | "utm_medium" | "utm_campaign" | "fbclid" | "gclid";

/** "Came from": the old panel's Campaign Attribution (#505, #506) on one line. */
export function cameFrom(q: Pick<QuoteRow, AttributionFields>): string {
  const parts: string[] = [];
  if (q.square_source) parts.push("Square (imported invoice)");
  else if (q.source) parts.push(SOURCE_LABELS[q.source] ?? q.source);
  const utm = [q.utm_source, q.utm_medium, q.utm_campaign].filter((v): v is string => !!v);
  parts.push(...utm);
  // Click-id-only traffic: paid clicks with no UTM tags on the ad.
  if (utm.length === 0 && (q.fbclid || q.gclid)) parts.push(q.fbclid ? "Paid click (Meta)" : "Paid click (Google)");
  return parts.length ? parts.join(" · ") : "Not recorded";
}

/** The tax row reads the quote's stored rate, so a Square quote shows its own. */
export function taxLabel(rate: number | null | undefined): string {
  return `HST ${rate == null ? 13 : Number(rate)}%`;
}

/** The event strip's guest count: "1 guest", "38 guests". */
export function guestCountLabel(count: number | null | undefined): string {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? "guest" : "guests"}`;
}

/** False on a row read before the #1559 migration is live. */
export function pendingEtransferSupported(row: object): boolean {
  return "pending_etransfer_amount" in row;
}

/** Delete draft's re-check: nothing changed since the confirm was shown. */
export function draftUnchangedSince(
  shown: { itemCount: number; total: number },
  fresh: { status: string; total: number | null; amount_paid: number | null; line_items?: { id: string }[] | null },
): boolean {
  const sameItems = (fresh.line_items?.length ?? 0) === shown.itemCount;
  return fresh.status === "draft" && num(fresh.amount_paid) === 0 && sameItems && Math.abs(num(fresh.total) - num(shown.total)) < 0.005;
}
